import { getTitleAndEmoji } from '@confluence/emoji-title/entry-points/helpers.ts';
import {
	getEmojiDraftFromProperties,
	getEmojiPublishedFromProperties,
} from '@confluence/emoji-title/entry-points/transformer.ts';
import { getURLBypassingResumeDraftAction } from '@confluence/content-utils/entry-points/resumeDraftActionBypass.ts';
import { getIdFromConfluenceContentAri } from '@confluence/content-types-utils/entry-points/getIdFromConfluenceContentAri.ts';

import type { GlobalPageCard, SpacePageCard, PageCardBase } from './card-types.ts';

const ALL_CONTENT_TYPE = ['page', 'blogpost', 'whiteboard', 'database', 'embed', 'folder', 'slide'];

const resumeDraftWebUiBypass = (urlPath, content): string => {
	const bypassUrl = getURLBypassingResumeDraftAction({
		url: urlPath || '',
		spaceKey: content?.space?.alias || content?.space?.key || '',
		contentType: content?.type,
		editorVersion: content?.editorVersion?.nodes?.[0]?.value?.replace(/['"]+/g, ''),
		contentId: content?.id || '',
		noContextPath: true,
	});

	return bypassUrl;
};

export const transformBaseContent = (
	content: any,
	untitledDraftText?: any,
	isWhiteboardEmojiFGEnabled?: any,
	isActivityV3HomeRecent?: any,
): PageCardBase => {
	const EMOJI_CONTENT_TYPE = isWhiteboardEmojiFGEnabled
		? ['page', 'blogpost', 'embed', 'whiteboard', 'slide']
		: ['page', 'blogpost', 'embed'];

	const contentType = ALL_CONTENT_TYPE.includes(content?.type) ? content.type : 'page';

	if (isActivityV3HomeRecent) {
		return {
			id: getIdFromConfluenceContentAri({ ari: content?.id, resourceType: contentType }) || '',
			status:
				content?.status === 'draft' || content?.status === 'unpublished'
					? content?.status
					: undefined,
			// @ts-expect-error TODO FIXME there is some deep null mismatch, which might be worth fixing
			title: getTitleAndEmoji(
				content?.title,
				content?.metadata?.titleEmojiPublished?.value || '',
				content?.metadata?.titleEmojiDraft?.value || '',
				content?.status,
				untitledDraftText,
				EMOJI_CONTENT_TYPE.includes(contentType),
			),
			type: contentType,
			subType: content?.subtype,
			editorVersion: content?.editorVersion,
			links: {
				editui: resumeDraftWebUiBypass(content?.links?.editUi, content),
				webui: resumeDraftWebUiBypass(content?.links?.webUi, content),
			},
			isStarred: content?.viewer?.favoritedSummary?.isFavorite || false,
			likesCount: content?.likesSummary?.count || 0,
			commentsCount: content?.commentCountSummary?.total || 0,
			excerpt: content?.body?.excerpt?.trim(),
		};
	}

	return {
		id: content?.id || '',
		status:
			content?.status === 'draft' || content?.status === 'unpublished'
				? content?.status
				: undefined,
		// @ts-expect-error TODO FIXME there is some deep null mismatch, which might be worth fixing
		title: getTitleAndEmoji(
			content?.title,
			getEmojiPublishedFromProperties(content),
			getEmojiDraftFromProperties(content),
			content?.status,
			untitledDraftText,
			EMOJI_CONTENT_TYPE.includes(contentType),
		),
		type: contentType,
		subType: content?.subType,
		editorVersion: content?.editorVersion,
		links: {
			editui: resumeDraftWebUiBypass(content?.links?.editui, content),
			webui: resumeDraftWebUiBypass(content?.links?.webui, content),
		},
		isStarred: content?.metadata?.currentuser?.favourited?.isFavourite || false,
		likesCount: content?.likes?.count || 0,
		commentsCount: content?.metadata?.comments?.commentsCount || 0,
		excerpt: content?.excerpt?.trim(),
		lastAction: {
			when: content?.metadata?.lastModifiedDate || '',
			action: 'updated',
		},
	};
};

export const transformGlobalContent = (
	content: any,
	untitledDraftText?: any,
	isWhiteboardEmojiFGEnabled?: any,
	isActivityV3HomeRecent = false,
): GlobalPageCard => {
	const spaceAlias = content?.space?.alias || content?.space?.key;
	return {
		...transformBaseContent(
			content,
			untitledDraftText,
			isWhiteboardEmojiFGEnabled,
			isActivityV3HomeRecent,
		),
		space: {
			spaceKey: spaceAlias || '',
			name: content?.space?.name || '',
		},
		creatorId: content?.creatorId || '',
		history: {
			ownedBy: {
				displayName: content?.history?.ownedBy?.displayName || '',
			},
		},
	};
};

export const transformSpaceContent = (
	content: any,
	untitledDraftText: string = '',
	isWhiteboardEmojiFGEnabled = false,
): SpacePageCard => {
	const createdBy =
		content?.status === 'draft' ? content?.version?.by : content?.history?.createdBy;
	const ownedBy = content?.status === 'draft' ? content?.version?.by : content?.history?.ownedBy;
	return {
		...transformBaseContent(content, untitledDraftText, isWhiteboardEmojiFGEnabled),
		createdBy: {
			displayName: createdBy?.displayName || '',
			profilePicture: createdBy?.profilePicture?.path || '',
			accountId: createdBy?.accountId || '',
		},
		ownedBy: {
			displayName: ownedBy?.displayName || '',
			profilePicture: ownedBy?.profilePicture?.path || '',
			accountId: ownedBy?.accountId || '',
		},
	};
};

export const isSpacePageCard = (card: PageCardBase): card is SpacePageCard =>
	Boolean((card as SpacePageCard).createdBy);
